import { EventEmitter } from 'fbemitter';
import * as React from 'react';
export class Network extends EventEmitter {
    constructor() {
        super(...arguments);
        this.curTime = 0;
        this.nextId = 0;
        this.cells = {};
        this.evalStack = [];
        this.debug = false;
        this.sampling = false;
    }
    input(initialValue) {
        const name = "__INPUT_" + this.getId();
        const cell = this.cells[name] = {
            name,
            isAnchor: true,
            changeTime: this.curTime,
            isEvidenceFor: new Set(),
            type: 'constant',
            value: initialValue,
        };
        return {
            get: () => {
                return this._evaluateCell(cell);
            },
            set: (newValue) => {
                cell.value = newValue;
                this.curTime++;
                cell.changeTime = this.curTime;
                this.emit('tick');
            },
            delete: () => {
                cell.isAnchor = false;
                this._garbageCollect(cell);
            },
        };
    }
    value(name, func, valuesEqual) {
        if (typeof name !== 'string') {
            throw new Error('value needs a name!');
        }
        if (!this.evalStack.length) {
            throw new Error('value cannot be called outside of an output cell!');
        }
        let cell = this.cells[name];
        if (!cell) {
            cell = this.cells[name] = {
                name,
                isAnchor: false,
                changeTime: null,
                isEvidenceFor: new Set(),
                type: 'computation',
                func,
                valuesEqual,
                status: null,
                evidence: new Set(),
            };
        }
        return this._evaluateCell(cell);
    }
    valueFunc(name, func, valuesEqual) {
        if (typeof name !== 'string') {
            throw new Error('value needs a name!');
        }
        return (...args) => {
            const cellName = `${name}(${args.map((arg) => JSON.stringify(arg)).join()})`;
            return this.value(cellName, () => func(...args), valuesEqual);
        };
    }
    output(func, debugName) {
        const name = "__OUTPUT_" + (debugName ? debugName + "_" : "") + this.getId();
        const cell = this.cells[name] = {
            name,
            isAnchor: true,
            changeTime: null,
            isEvidenceFor: new Set(),
            type: 'computation',
            func,
            status: null,
            evidence: new Set(),
        };
        return new OutputCell(cell, this);
    }
    sample(func) {
        if (this.sampling) {
            return func();
        }
        else {
            this.sampling = true;
            const outputCell = this.output(func);
            const result = outputCell.get();
            outputCell.delete();
            this.sampling = false;
            return result;
        }
    }
    // private stuff
    getId() {
        const id = this.nextId;
        this.nextId++;
        return id;
    }
    _evaluateCell(cell) {
        // If this is being called from another cell, mark the dependence
        if (this.evalStack.length) {
            const callingCell = this.evalStack[this.evalStack.length - 1];
            callingCell.evidence.add(cell);
            cell.isEvidenceFor.add(callingCell);
        }
        // If this is a constant cell, do it real quick 'n' easy
        if (cell.type === 'constant') {
            return cell.value;
        }
        // Now we can assume we have a computation cell
        // If the cell is patently up-to-date, we're done
        if (cell.status && cell.status.valueTime === this.curTime) {
            return cell.status.value;
        }
        // If this is the first evaluation of the cell, do it real quick 'n' easy
        if (!cell.status) {
            this.evalStack.push(cell);
            if (this.debug) {
                console.group("🌸 " + cell.name);
            }
            let newValue;
            try {
                newValue = cell.func();
            }
            catch (e) {
                console.error(e);
                throw e;
            }
            finally {
                if (this.debug) {
                    console.log("→", newValue);
                    console.groupEnd();
                }
                this.evalStack.pop();
            }
            cell.changeTime = this.curTime;
            cell.status = {
                value: newValue,
                valueTime: this.curTime,
            };
            return cell.status.value;
        }
        // Otherwise, check if any of its ostensible evidence cells have changed
        let evidenceCellChanged = undefined;
        for (const evidenceCell of cell.evidence) {
            this._evaluateCell(evidenceCell);
            if (evidenceCell.changeTime > cell.status.valueTime) {
                evidenceCellChanged = evidenceCell;
                break;
            }
        }
        if (evidenceCellChanged) {
            const oldValue = cell.status.value;
            const oldEvidence = cell.evidence;
            cell.evidence = new Set();
            this.evalStack.push(cell);
            if (this.debug) {
                console.group("🌸 " + cell.name);
                console.log("input changed: ", evidenceCellChanged.name, getStoredValue(evidenceCellChanged));
            }
            let newValue;
            try {
                newValue = cell.func();
            }
            catch (e) {
                console.error(e);
                throw e;
            }
            finally {
                if (this.debug) {
                    console.log("→", newValue);
                    console.groupEnd();
                }
                this.evalStack.pop();
                oldEvidence.forEach((oldEvidenceCell) => {
                    if (!cell.evidence.has(oldEvidenceCell)) {
                        this.isNoLongerEvidenceFor(oldEvidenceCell, cell);
                    }
                });
            }
            if (cell.valuesEqual ? !cell.valuesEqual(oldValue, newValue) : oldValue !== newValue) {
                cell.status.value = newValue;
                cell.changeTime = this.curTime;
                // this.emit('change', {cell, oldValue, newValue})
            }
        }
        cell.status.valueTime = this.curTime;
        return cell.status.value;
    }
    isNoLongerEvidenceFor(oldEvidenceCell, cell) {
        oldEvidenceCell.isEvidenceFor.delete(cell);
        this._garbageCollect(oldEvidenceCell);
    }
    _garbageCollect(cell) {
        if (cell.isEvidenceFor.size === 0 && !cell.isAnchor) {
            delete this.cells[cell.name];
            if (cell.type === 'computation') {
                cell.evidence.forEach((oldEvidenceCell) => {
                    this.isNoLongerEvidenceFor(oldEvidenceCell, cell);
                });
            }
        }
    }
}
export class OutputCell extends EventEmitter {
    // private _tickListener: EventSubscription
    // private _changeListener: EventSubscription
    constructor(cell, network) {
        super();
        this._cell = cell;
        this._network = network;
        // TEMP: output cells are now evaluated eagerly
        // this should probably be configurable
        // this._tickListener = this._network.addListener('tick', () => this.get())
        // this._changeListener = this._network.addListener('change', ({cell, oldValue, newValue}: any) => {
        //   if (cell === this._cell) {
        //     this.emit('change', {oldValue, newValue})
        //   }
        // })
    }
    get() {
        if (this._network.evalStack.length) {
            console.log("evalStack", this._network.evalStack);
            console.log("current cell", this._cell);
            throw new Error('output cannot be called inside a cell!');
        }
        return this._network._evaluateCell(this._cell);
    }
    delete() {
        this._cell.isAnchor = false;
        this._network._garbageCollect(this._cell);
        // this._tickListener.remove()
        // this._changeListener.remove()
    }
}
function getStoredValue(cell) {
    if (cell.type === 'constant') {
        return cell.value;
    }
    else {
        return cell.status && cell.status.value;
    }
}
export const NetworkContextElems = React.createContext(undefined);
export class NetworkProvider extends React.Component {
    // upcomingUpdate: number
    constructor(props) {
        super(props);
        // props.network.addListener('tick', () => {
        //   if (this.upcomingUpdate) {
        //     cancelAnimationFrame(this.upcomingUpdate)
        //   }
        //   this.upcomingUpdate = requestAnimationFrame(() => {
        //     console.log("FORCING UPDATE")
        //     this.forceUpdate()
        //   })
        // })
    }
    render() {
        const { network, propsChanged, children } = this.props;
        return React.createElement(NetworkContextElems.Provider, { value: { network, propsChanged } }, children);
    }
}
export const NetworkUser = (props) => React.createElement(NetworkContextElems.Consumer, null, (networkContext) => {
    if (!networkContext) {
        throw new Error("Cannot use NetworkUser outside of a NetworkProvider");
    }
    return React.createElement(NetworkUserHelper, { networkContext: networkContext, userProps: props });
});
class NetworkUserHelper extends React.Component {
    constructor() {
        super(...arguments);
        this.cellShouldUpdate = false;
    }
    shouldComponentUpdate(nextProps) {
        // Fun little hack: we use this to figure out when to rewrite the cell
        this.cellShouldUpdate = this.shouldCellUpdate(nextProps);
        return true;
    }
    shouldCellUpdate(nextProps) {
        // State is delicate – we assume any setState might break our cell
        if (nextProps.userProps.state !== this.props.userProps.state) {
            return true;
        }
        // Hopefully we have a propsChanged function, and can use that to compare props...
        if (this.props.networkContext.propsChanged) {
            if (this.props.networkContext.propsChanged(nextProps.userProps.props, this.props.userProps.props)) {
                return true;
            }
        }
        else if (nextProps.userProps.props !== this.props.userProps.props) {
            return true;
        }
        return false;
    }
    render() {
        const { userProps: { render, debugName }, networkContext: { network } } = this.props;
        if (!this.outputCell || this.cellShouldUpdate) {
            this.cellShouldUpdate = false;
            const newOutputCell = network.output(render, debugName);
            const result = newOutputCell.get();
            if (this.outputCell) {
                this.outputCell.delete();
            }
            this.outputCell = newOutputCell;
            return result;
        }
        else {
            return this.outputCell.get();
        }
    }
    componentWillUnmount() {
        if (this.outputCell) {
            this.outputCell.delete();
        }
    }
}
//# sourceMappingURL=index.js.map


//////////////////
// WEBPACK FOOTER
// ../golgi/build/index.js
// module id = 4
// module chunks = 0